import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, FileText, Globe2, Phone, Tags } from 'lucide-react';
import { get, post, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { COUNTRY_BASICS, SUPPORTED_COUNTRIES, countryProfile, type CountryCode } from '@his/shared';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Button, Field, Input, Select } from '@/components/ui/primitives';
import { PhoneText } from '@/components/ui/phone-text';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { moduleName, orgReturnTo, type Branch } from './org-types';
import { mainCategoryOptions, openingCategories, subCategoryOptions, toggleMainCategory, toggleSubCategory } from './branch-categories';

export function BranchFormPage() {
  const { branchId = '' } = useParams();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), orgReturnTo('branches'));

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => get<Branch[]>('/org/branches?all=1'),
  });
  const branch = branches.data?.find((row) => row.id === branchId) ?? null;

  if (!branches.data || (branchId && !branch)) {
    return (
      <FormScreen title={branchId ? tr('admin', 'loading') : tr('admin', 'add_a_branch')} icon={Building2} backTo={returnTo}>
        <RecordState error={branches.error} missing={Boolean(branches.data)} />
      </FormScreen>
    );
  }

  return <BranchForm key={branchId || 'new'} branch={branch} branches={branches.data} returnTo={returnTo} />;
}

function BranchForm({ branch, branches, returnTo }: { branch: Branch | null; branches: Branch[]; returnTo: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user, refreshUser, hasModule } = useAuth();
  const defaultCountry = branch?.country ?? user?.branches.find((b) => b.country)?.country ?? '';
  const [opening] = useState(() => openingCategories(branch?.categoryModules ?? [], hasModule));
  const [initial] = useState(() => ({
    code: branch?.code ?? '',
    name: branch?.name ?? '',
    city: branch?.city ?? '',
    phone: branch?.phone ?? '',
    address: branch?.address ?? '',
    country: defaultCountry,
    parentBranchId: branch?.parentBranchId ?? '',
    categoryModules: opening.categories,
    letterheadLine: branch?.letterheadLine ?? '',
    subdivision: branch?.subdivision ?? '',
    nameLocal: branch?.nameLocal ?? '',
    addressLocal: branch?.addressLocal ?? '',
  }));
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const hasChildren = Boolean(branch && branches.some((other) => other.parentBranchId === branch.id));
  const parentOptions = branches.filter((other) => !other.parentBranchId && other.id !== branch?.id);
  const mainOptions = mainCategoryOptions(form.categoryModules, hasModule);
  const subGroups = mainOptions
    .filter((main) => form.categoryModules.includes(main))
    .map((main) => ({ main, subs: subCategoryOptions(main, form.categoryModules, hasModule) }))
    .filter((group) => group.subs.length > 0);

  const pickParent = async (parentBranchId: string) => {
    setForm((current) => ({ ...current, parentBranchId }));
    if (!parentBranchId || branch) return;
    try {
      const suggestion = await get<{ parentCode: string; code: string }>(`/org/branches/${parentBranchId}/next-child-code`);
      setForm((current) => (current.parentBranchId === parentBranchId ? { ...current, code: suggestion.code } : current));
    } catch (caught) {
      setError(describeError(caught));
    }
  };
  const countryLocked = Boolean(branch?.taxConfigured && branch.country);
  const profile = countryProfile(form.country || null);
  const subdivisionRequired = profile.branchSubdivision === 'required';
  const localLanguage = profile.locales.documentSecondary === 'ar' ? 'Arabic' : profile.locales.documentSecondary;
  const countryChanged = form.country !== (branch?.country ?? '');
  const shownCurrency =
    branch && !countryChanged
      ? branch.currency ?? '—'
      : form.country
        ? COUNTRY_BASICS[form.country as CountryCode]?.currency ?? '—'
        : '—';

  const create = useMutation({
    mutationFn: () => {
      const payload = {
        code: form.code.toUpperCase(),
        name: form.name,
        city: form.city || undefined,
        phone: form.phone || undefined,
        address: form.address || undefined,
        ...(form.country && (!branch || countryChanged) ? { country: form.country } : {}),
        parentBranchId: form.parentBranchId || null,
        categoryModules: form.categoryModules,
        letterheadLine: form.letterheadLine.trim() || null,
        subdivision: profile.subdivisions.length ? form.subdivision || null : null,
        nameLocal: localLanguage ? form.nameLocal.trim() || null : undefined,
        addressLocal: localLanguage ? form.addressLocal.trim() || null : undefined,
        isActive: branch?.isActive ?? true,
      };
      return branch ? put(`/org/branches/${branch.id}`, payload) : post<Branch>('/org/branches', payload);
    },
    onSuccess: (saved) => {
      const pending = !branch && (saved as Branch | undefined)?.approvalStatus === 'pending';
      toast.success(
        branch ? tr('admin', 'branch_updated') : tr('admin', 'branch_created'),
        pending ? tr('admin', 'its_staff_can_sign_in_to_it_once_the_owner') : undefined,
      );
      void queryClient.invalidateQueries({ queryKey: ['branches'] });
      void refreshUser();
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = Boolean(form.code && form.name && (branch || form.country) && (branch || !subdivisionRequired || form.subdivision));
  const dirty = !create.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title={branch ? tr('admin', 'edit_x', { name: branch.name }) : tr('admin', 'add_a_branch')}
      description={branch ? tr('admin', 'the_city_is_what_patients_search_for_on_the') : tr('admin', 'the_code_prefixes_mrns_issued_at_this_site')}
      icon={Building2}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        create.mutate();
      }}
    >
      <FormSection title={tr('admin', 'identity')} icon={Building2}>
        {parentOptions.length > 0 && !hasChildren && (
          <Field
            label={tr('admin', 'sits_under')}
            className="sm:col-span-2"
            hint={tr('admin', 'leave_empty_for_a_main_branch_a_sub_branch_gets')}
          >
            <Select value={form.parentBranchId} onChange={(e) => void pickParent(e.target.value)}>
              <option value="">{tr('admin', 'a_main_branch')}</option>
              {parentOptions.map((parent) => (
                <option key={parent.id} value={parent.id}>
                  {parent.code} · {parent.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field
          label={tr('admin', 'code')}
          required
          hint={
            hasChildren
              ? tr('admin', 'fixed_while_it_has_sub_branches_their_codes')
              : 'Short code; printed wherever a number pattern uses {BRANCH}'
          }
        >
          <Input
            value={form.code}
            disabled={hasChildren}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
            maxLength={16}
          />
        </Field>
        <Field label={tr('admin', 'name')} required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('admin', 'contact')} icon={Phone}>
        <Field label={tr('admin', 'city')}>
          <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        </Field>
        <Field label={tr('admin', 'phone')}>
          <PhoneText value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </Field>
        <Field label={tr('admin', 'address')} className="sm:col-span-2">
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('admin', 'country')} icon={Globe2}>
        <Field
          label={tr('admin', 'country')}
          required={!branch}
          hint={
            countryLocked
              ? tr('admin', 'locked_this_branch_already_has_its_tax')
              : tr('admin', 'decides_the_currency_tax_rules_and_id_documents')
          }
        >
          <Select
            value={form.country}
            disabled={countryLocked}
            onChange={(e) => setForm({ ...form, country: e.target.value, subdivision: '' })}
          >
            <option value="">{tr('admin', 'select_a_country')}</option>
            {SUPPORTED_COUNTRIES.map((code) => (
              <option key={code} value={code}>
                {COUNTRY_BASICS[code].name}
                {COUNTRY_BASICS[code].readiness === 'preview' ? ' (preview)' : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('admin', 'currency')} hint={tr('admin', 'follows_the_country')}>
          <Input value={shownCurrency} disabled readOnly />
        </Field>
        {profile.subdivisions.length > 0 && (
          <Field
            label={form.country === 'AE' ? tr('admin', 'emirate') : tr('admin', 'province_or_region')}
            required={subdivisionRequired}
            hint={subdivisionRequired ? tr('admin', 'decides_the_health_regulator_and_the_claims_add') : tr('admin', 'needed_where_the_branch_is_registered_for')}
          >
            <Select value={form.subdivision} onChange={(e) => setForm({ ...form, subdivision: e.target.value })}>
              <option value="">{subdivisionRequired ? tr('admin', 'select') : tr('admin', 'not_set')}</option>
              {profile.subdivisions.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </FormSection>

      <FormSection title={tr('admin', 'documents')} icon={FileText}>
        {localLanguage && (
          <>
            <Field label={tr('admin', 'name_x', { localLanguage: localLanguage })} hint={tr('admin', 'printed_beside_the_english_name_on_bilingual')}>
              <Input dir="auto" value={form.nameLocal} maxLength={160} onChange={(e) => setForm({ ...form, nameLocal: e.target.value })} />
            </Field>
            <Field label={tr('admin', 'address_x', { localLanguage: localLanguage })}>
              <Input dir="auto" value={form.addressLocal} maxLength={400} onChange={(e) => setForm({ ...form, addressLocal: e.target.value })} />
            </Field>
          </>
        )}
        <Field
          label={tr('admin', 'letterhead_line')}
          className="sm:col-span-2"
          hint={tr('admin', 'printed_on_this_branchs_documents_under_the')}
        >
          <Input value={form.letterheadLine} maxLength={200} onChange={(e) => setForm({ ...form, letterheadLine: e.target.value })} />
        </Field>
      </FormSection>

      {mainOptions.length > 0 && (
        <FormSection
          title={tr('admin', 'what_this_branch_is')}
          description={tr('admin', 'for_the_list_and_for_suggesting_its_standard')}
          icon={Tags}
          columns={1}
        >
          <Field label={tr('admin', 'main_category')} hint={tr('admin', 'pick_the_main_categories_first_their_sub')}>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {mainOptions.map((main) => {
                const on = form.categoryModules.includes(main);
                return (
                  <button
                    key={main}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setForm({ ...form, categoryModules: toggleMainCategory(form.categoryModules, main) })}
                    className={`rounded-lg border px-3 py-2 text-start text-sm font-medium transition-colors ${
                      on ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-background hover:bg-accent'
                    }`}
                  >
                    {moduleName(main)}
                  </button>
                );
              })}
            </div>
          </Field>
          {subGroups.map((group) => (
            <Field key={group.main} label={tr('admin', 'x_sub_categories', { name: moduleName(group.main) })}>
              <div className="flex flex-wrap gap-1.5">
                {group.subs.map((sub) => {
                  const on = form.categoryModules.includes(sub);
                  return (
                    <Button
                      key={sub}
                      type="button"
                      size="sm"
                      variant={on ? 'default' : 'outline'}
                      onClick={() => setForm({ ...form, categoryModules: toggleSubCategory(form.categoryModules, sub) })}
                    >
                      {moduleName(sub)}
                    </Button>
                  );
                })}
              </div>
            </Field>
          ))}
          {opening.mainInferred && <p className="text-xs text-muted-foreground">{tr('admin', 'its_main_category_was_filled_in_from_its_sub')}</p>}
        </FormSection>
      )}

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={branch ? tr('admin', 'save_changes') : tr('admin', 'create_branch')}
        loading={create.isPending}
        incomplete={!ready}
        cancelTo={returnTo}
      />
    </FormScreen>
  );
}
